import useTranslation from 'next-translate/useTranslation'
import { Dispatch, SetStateAction, useEffect } from 'react'

import { UserAuthentication, UserAuthTouchPoint } from '@/components/auth/UserAuthentication'
import { ConceptImage } from '@/components/ConceptImage'
import LoginModalForm, { loginModalFormOptions } from '@/components/Modal/Types/LoginModalForm'
import { VisitRequestModal } from '@/containers/PriorityDetail/sections/RequestVisit/VisitRequestForm'
import { useExpressPotentialPropertyVisitInterest } from '@/containers/PriorityDetail/useExpressPotentialPropertyVisitInterest'
import { useAuthentication } from '@/context/AuthProvider'
import { useBottomSheet } from '@/context/BottomSheetContext'
import { useDevice } from '@/context/Device'
import { useModal } from '@/context/ModalContext'
import { Button } from '@/design-lib/components/Button'
import { TENANTS } from '@/lib/tenants/constants'

import { useVisitInterestBeforeLogin } from '../../useVisitInterestBeforeLogin'

type Props = {
  className?: string
  gtmTouchPoint: UserAuthTouchPoint
  handleCtaClick: VoidFunction
  hasFullLabelOnMobile?: boolean
  isFullWidth?: boolean
  setShowToastError: Dispatch<SetStateAction<boolean>>
  thankYouPageUrl: string
  transactionId: string
}

export const RequestVisitCTA: React.FC<Props> = ({
  gtmTouchPoint,
  handleCtaClick,
  hasFullLabelOnMobile,
  isFullWidth = true,
  setShowToastError,
  thankYouPageUrl,
  transactionId,
  ...props
}) => {
  const { isLoading, user } = useAuthentication()

  const { t } = useTranslation()
  const device = useDevice()
  const ctaLabel =
    device === 'desktop' || hasFullLabelOnMobile
      ? t('priority:Priority_DetailPage_FloatingBanner_CTA')
      : t('priority:Priority_DetailPage_FloatingBanner_CTAmobile')

  const { openModal } = useModal()
  const { closeBottomSheet, openBottomSheet } = useBottomSheet()
  const { resetVisitInterestBeforeLogin, setVisitInterestBeforeLoginToTrue, visitInterestBeforeLogin } =
    useVisitInterestBeforeLogin()
  const expressVisit = useExpressPotentialPropertyVisitInterest(transactionId)

  const openVisitRequestModal = () => {
    openModal(
      <VisitRequestModal
        expressVisitInterest={(args) => expressVisit({ args, tenant: TENANTS.casavo })}
        setShowToastError={setShowToastError}
        thankYouPageUrl={thankYouPageUrl}
      />,
      {
        overlay: true,
      }
    )
  }

  useEffect(() => {
    if (user && !isLoading && visitInterestBeforeLogin !== null) {
      resetVisitInterestBeforeLogin()
      device === 'mobile' && closeBottomSheet()
      openVisitRequestModal()
    }
  }, [user, isLoading, visitInterestBeforeLogin])

  const handleVisitInterestClick = () => {
    handleCtaClick()
    if (!user?.id && !isLoading) {
      setVisitInterestBeforeLoginToTrue()

      if (device === 'mobile') {
        openBottomSheet(
          <UserAuthentication
            conceptImage={<ConceptImage height={75} name="write" width={75} />}
            mainTitle="Login_PriorityDetail_BottomSheet_Title"
            returnUrl={window.location.href}
            touchPoint={gtmTouchPoint}
          />,
          { bgColor: 'offWhite', isPriority: true, minHeight: 610 }
        )
      } else {
        openModal(
          <LoginModalForm
            isPriority
            bgColor="offWhite"
            conceptImage={<ConceptImage height={120} name="write" width={120} />}
            mainTitle="Login_PriorityDetail_BottomSheet_Title"
            returnURL={window.location.href}
            touchPoint={gtmTouchPoint}
          />,
          loginModalFormOptions
        )
      }
    } else {
      openVisitRequestModal()
    }
  }

  return (
    <Button
      data-testid={gtmTouchPoint}
      fullWidth={isFullWidth}
      label={ctaLabel}
      minWidth={200}
      onClick={handleVisitInterestClick}
      {...props}
    />
  )
}
